import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Form, Formik, FormikErrors } from "formik";
import * as React from "react";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import ErrorMsg from "../../components/ErrorMsg";
import ProgressIndicator from "../../components/ProgressIndicator";
import strings from "../../config/localization";
import { useNewTimeslotMutation } from "../../services/appln";
import { convertDate } from "../util";

interface FormData {
  name: string;
  desc: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

interface Props {
  onSubmitHandler: (newObj: any) => void;
  onCancelHandler: () => void;
  state: boolean;
  bandobastId: string;
}

export default function NewTimeslot(props: Props) {
  const [createTimeslot, createTimeslotParams] = useNewTimeslotMutation();

  const iv: FormData = {
    name: "",
    desc: "",
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
  };

  const onCancelHandlerWrapper = () => {
    createTimeslotParams.reset();
    props.onCancelHandler();
  };

  console.log("createTimeslotParams", createTimeslotParams);
  React.useEffect(() => {
    if (createTimeslotParams.isSuccess) {
      props.onSubmitHandler(createTimeslotParams.data.id);
    }
  }, [createTimeslotParams]);

  return (
    <div>
      <Dialog
        open={props.state}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">{strings.newTimeslot}</DialogTitle>
        {createTimeslotParams.isError && (
          <ErrorMsg msg={strings.failedToCreateTimeslot} />
        )}
        {createTimeslotParams.isLoading && <ProgressIndicator />}
        <DialogContent>
          <Formik
            initialValues={iv}
            validate={(values) => {
              const errors: FormikErrors<FormData> = {};
              const timeRegEx = /[0-9][0-9]:[0-9][0-9]/g;
              if (!values.startDate) {
                errors.startDate = strings.startDateMandatory;
              }
              if (!values.startTime) {
                errors.startTime = strings.startTimeMandatory;
              } else if (!values.startTime.match(timeRegEx)) {
                errors.startTime = strings.invalidTimeFormat;
              }
              if (!values.endDate) {
                errors.endDate = strings.endDateMandatory;
              }
              if (!values.endTime) {
                errors.endTime = strings.endTimeMandatory;
              } else if (!values.endTime.match(timeRegEx)) {
                errors.endTime = strings.invalidTimeFormat;
              }
              if (
                values.startDate &&
                values.startTime &&
                values.endDate &&
                values.endTime
              ) {
                const startTimestamp = Date.parse(
                  `${values.startDate}T${values.startTime}:00.000Z`,
                );
                const endTimestamp = Date.parse(
                  `${values.endDate}T${values.endTime}:00.000Z`,
                );
                if (startTimestamp >= endTimestamp) {
                  errors.startDate = strings.startDateLaterThanEndDate;
                }
              }
              // TODO: check if start time and end time fall within start date and end date of bandobast
              return errors;
            }}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false);
              const updatedValues = {
                name: values.name,
                desc: values.desc,
                startTime: `${convertDate(values.startDate)} ${
                  values.startTime
                }`,
                endTime: `${convertDate(values.endDate)} ${values.endTime}`,
              };
              createTimeslot({
                bandobastId: props.bandobastId,
                body: updatedValues,
              });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => (
              <Form>
                <Box sx={{ flexGrow: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        required
                        margin="normal"
                        id="name"
                        name="name"
                        label={strings.name}
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        margin="normal"
                        id="desc"
                        name="desc"
                        label={strings.description}
                        value={values.desc}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.desc && Boolean(errors.desc)}
                        helperText={touched.desc && errors.desc}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        required
                        margin="normal"
                        id="startDate"
                        type="date"
                        name="startDate"
                        label={strings.startDate}
                        value={values.startDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.startDate && Boolean(errors.startDate)}
                        helperText={touched.startDate && errors.startDate}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        required
                        margin="normal"
                        id="startTime"
                        name="startTime"
                        label={strings.startTime}
                        value={values.startTime}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.startTime && Boolean(errors.startTime)}
                        helperText={touched.startTime && errors.startTime}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        required
                        margin="normal"
                        id="endDate"
                        type="date"
                        name="endDate"
                        label={strings.endDate}
                        value={values.endDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.endDate && Boolean(errors.endDate)}
                        helperText={touched.endDate && errors.endDate}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        required
                        margin="normal"
                        id="endTime"
                        name="endTime"
                        label={strings.endTime}
                        value={values.endTime}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.endTime && Boolean(errors.endTime)}
                        helperText={touched.endTime && errors.endTime}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <ButtonGroup>
                        <Button
                          variant="contained"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {strings.create}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={onCancelHandlerWrapper}
                        >
                          {strings.cancel}
                        </Button>
                      </ButtonGroup>
                    </Grid>
                  </Grid>
                </Box>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
}
